import React from 'react';
import memoize from 'memoize-one';
import { compose, setDisplayName } from 'recompose';
import { Query, Mutation, ApolloConsumer } from 'react-apollo';
import { curry, get, find, isPlainObject, omit } from 'lodash';

import withRequestAssets from 'compositions/CaseRequestsPanel/RequestAssetsForm/withRequestAssets';
import { CASE_STATUS } from 'compositions/CaseStatus/constants';

import validateAssetQuery from './validateAssetQuery';
import caseAssetValidationQuery from './caseAssetValidationQuery';
import setCaseAssetValidationMutation from './setCaseAssetValidationMutation';
import { validationType } from '../constants';

const defaultValidationData = { type: 'MATCH_ASSET', notes: null, entries: {} };

// Only show entries for assets we know about, and transform
// from an array to an object whose keys are the asset ids.
const getValidationEntries = memoize((entries, primaryAsset, relatedAssets) => {
  if (!entries) return {};

  const getAttempts = assetId => {
    const entry = find(entries, { assetId }) || {};

    return entry.assetValidationEntryAttempts || [];
  };

  return [primaryAsset, ...relatedAssets]
    .filter(asset => !!get(asset, 'id') && !!get(asset, 'assetType'))
    .reduce((acc, { id }) => ({ ...acc, [id]: getAttempts(id) }), {});
});

const cleanUpAttempt = attempt => ({
  ...omit(attempt, ['user', '__typename']),
  input: attempt.input ? omit(attempt.input, '__typename') : null,
  asset: attempt.asset ? omit(attempt.asset, '__typename') : null,
});

function withSetAssetValidation(WrappedComponent, componentProps) {
  return (
    <Mutation mutation={setCaseAssetValidationMutation}>
      {(mutate, { loading }) => {
        const setCaseAssetValidation = data => {
          const { caseId, assetValidation } = componentProps;

          if (!caseId) {
            return Promise.reject(
              new Error('"setCaseAssetValidation" requires the "caseId" prop.'),
            );
          }

          const variables = {
            type: validationType.matchAsset,
            ...assetValidation,
            ...data,
            caseId,
          };

          // Transform back to an array, as the backend expects it
          if (isPlainObject(variables.entries)) {
            variables.entries = Object.entries(variables.entries).reduce(
              (acc, [assetId, attempts]) => {
                const cleanedAttempts = attempts.map(cleanUpAttempt);

                return [...acc, { assetId, attempts: cleanedAttempts }];
              },
              [],
            );
          }

          return mutate({ variables });
        };

        return (
          <WrappedComponent
            {...componentProps}
            setCaseAssetValidation={setCaseAssetValidation}
            isSettingCaseAssetValidation={!!loading}
          />
        );
      }}
    </Mutation>
  );
}

function withValidateAsset(WrappedComponent, componentProps) {
  return (
    <ApolloConsumer>
      {client => {
        const validateAsset = (input = {}) => {
          const { caseId } = componentProps;

          if (!caseId) {
            return Promise.reject(
              new Error('"validateAsset" requires the "caseId" prop.'),
            );
          }

          return client.query({
            query: validateAssetQuery,
            variables: { caseId, input: omit(input, '__typename') },
            fetchPolicy: 'network-only',
          });
        };

        const props = { ...componentProps, validateAsset };

        return withSetAssetValidation(WrappedComponent, props);
      }}
    </ApolloConsumer>
  );
}

function withAssetValidationData(WrappedComponent, componentProps) {
  const { caseNumber, primaryAsset, relatedAssets = [] } = componentProps;

  return (
    <Query
      skip={!caseNumber}
      query={caseAssetValidationQuery}
      variables={{ caseNumber }}
    >
      {({ data, loading }) => {
        const caseId = get(data, 'case.id');
        const program = get(data, 'case.inboundProgramNumber') || {};
        const caseStatus = get(data, 'case.status');

        const {
          isUsxpress = false,
          assetValidationFields,
          assetValidationInstructions = '',
          requiresValidationForAssetTypes,
          assetValidationFailureInstructions = '',
        } = program;

        let assetValidation = get(data, 'case.assetValidation');
        let { requiresAssetValidation = false } = program;

        if (assetValidation) {
          assetValidation = {
            ...assetValidation,
            type: assetValidation.type || validationType.matchAsset,
          };

          // Make entries an object whose keys are the request assets ids
          if (Array.isArray(assetValidation.assetValidationEntries)) {
            assetValidation = {
              ...omit(assetValidation, [
                'assetValidationEntries',
                '__typename',
              ]),
              entries: getValidationEntries(
                assetValidation.assetValidationEntries,
                primaryAsset,
                relatedAssets,
              ),
            };
          }
        }

        const validateAssetTypes = (
          requiresValidationForAssetTypes || []
        ).filter(rule => !!rule.requirements.length);

        if (
          !validateAssetTypes.length ||
          // Prevents cases created before the "Asset Validation" feature,
          // from requiring validation if the case has been moved forward.
          (caseStatus !== CASE_STATUS.new && assetValidation === null)
        ) {
          requiresAssetValidation = false;
        }

        const props = {
          ...componentProps,
          caseId,
          isUsxpress,
          assetValidation: assetValidation || defaultValidationData,
          assetValidationFields: assetValidationFields || [],
          requiresAssetValidation,
          isLoadingAssetValidation: !!loading,
          assetValidationInstructions,
          requiresValidationForAssetTypes: validateAssetTypes || [],
          assetValidationFailureInstructions,
        };

        return withValidateAsset(WrappedComponent, props);
      }}
    </Query>
  );
}

export default compose(
  setDisplayName('withCaseAssetValidation'),
  withRequestAssets,
  curry(withAssetValidationData),
);



// WEBPACK FOOTER //
// ./src/compositions/CaseAssetValidationPanel/withCaseAssetValidation/index.js